import * as Sentry from '@sentry/react';
import './i18n';
import { AuthProvider } from '@/auth/auth-provider';
import { Navigate, RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
// Import the generated route tree
import { routeTree } from './routeTree.gen';

import { validateEnv } from './utils/env';

import './index.css';
import { ApiProvider } from '@/api';

if (import.meta.env.MODE === 'production') {
  Sentry.init({
    dsn: 'https://05df1ad6cab45d42aea25a9fcd05a164@o4509042083954688.ingest.us.sentry.io/4509347999514624',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    environment: import.meta.env.MODE,
    sendDefaultPii: true,
  });
}

window.addEventListener('error', (event) => {
  const msg = event.message ?? '';
  if (msg.includes('Loading chunk') || msg.includes("Unexpected token '<'")) {
    window.location.reload();
  }
});

validateEnv();

const applyDocumentLanguageSettings = () => {
  const { documentElement, head } = document;

  if (documentElement) {
    documentElement.lang = 'auto';
  }

  if (
    head &&
    !document.querySelector('meta[name="google"][content="notranslate"]')
  ) {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'google');
    meta.setAttribute('content', 'notranslate');
    head.appendChild(meta);
  }
};

applyDocumentLanguageSettings();

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => {
    return <Navigate to="/" replace />;
  },
  scrollRestoration: true,
  scrollRestorationBehavior: 'smooth',
  defaultPreload: 'intent',
  context: {},
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function App() {
  return <RouterProvider router={router} />;
}

// Render the app
const rootElement = document.getElementById('root');
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <AuthProvider>
        <ApiProvider>
          <App />
        </ApiProvider>
      </AuthProvider>
    </StrictMode>,
  );
}
